/**
 * UPMI — bespoke Gallery page (handoff Page 3): image carousel (prev/next +
 * dots) + designed testimonial image-cards + social band. Real brand imagery
 * from public/tenants/upmi.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { UpmiShell } from './shell';
import { INK, LINE, MEDIA, MUTED, O, OL, SOCIAL } from './tokens';

const SLIDES = Array.from({ length: 14 }, (_, i) => `${MEDIA}/gallery/image${i + 1}.jpg`);
const TESTI = Array.from({ length: 10 }, (_, i) => `${MEDIA}/testimonials/testi${i + 1}.jpg`);
const ICONS: [keyof typeof SOCIAL, string][] = [
  ['facebook', `${MEDIA}/icons/facebook.svg`],
  ['instagram', `${MEDIA}/icons/instagram.svg`],
  ['tiktok', `${MEDIA}/icons/tiktok.svg`],
];

/**
 * The carousel frame adopts each slide's own aspect ratio instead of forcing a
 * fixed one. It used to be a hard `16 / 9` box with `objectFit: 'cover'` and no
 * `objectPosition`, while every shipped source is SQUARE — so cover scaled each
 * image to the frame's width and kept only 9/16 = 56.25% of its height, slicing
 * ~21.9% off the top and ~21.9% off the bottom of all 14. The crop was identical
 * everywhere; whether a slide "looked fine" came down to whether its composition
 * survived losing both fifths, which is why some read as fine and others as
 * badly zoomed in.
 *
 * Ratios are measured from the decoded image rather than hardcoded, so replacing
 * a slide with a differently-shaped file needs no code change. Clamped to a sane
 * band so one freak image can't make the page absurdly tall or letterbox-thin,
 * and `objectFit: 'contain'` backs it up: anything the clamp refuses to match is
 * shown whole against the brand wash rather than cropped.
 */
const RATIO_MIN = 3 / 4;
const RATIO_MAX = 16 / 9;
/** Every slide shipped today is 1:1, so squares are the no-jump default. */
const RATIO_FALLBACK = 1;

export function UpmiGallery(): JSX.Element {
  const [slide, setSlide] = useState(0);
  const [ratios, setRatios] = useState<Record<number, number>>({});
  const n = SLIDES.length;
  const go = (d: number) => setSlide((s) => (s + d + n) % n);
  // Neighbours are mounted alongside the current slide, so by the time one is
  // navigated to, its ratio has already been measured — no resize flash.
  const activeRatio = ratios[slide] ?? RATIO_FALLBACK;

  return (
    <UpmiShell active="gallery">
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '20px 16px 64px' }}>
        <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 18 }}>
          <Link to="/" style={{ color: MUTED, textDecoration: 'none' }}>
            Home
          </Link>
          <span style={{ margin: '0 8px', color: '#ccc' }}>›</span>
          <span style={{ color: INK }}>Gallery</span>
        </div>

        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto 30px' }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '.2em',
              textTransform: 'uppercase',
              color: O,
            }}
          >
            The PROMAG300 Story
          </div>
          <h1
            style={{
              fontSize: 'clamp(28px,3.4vw,38px)',
              fontWeight: 800,
              margin: '8px 0 10px',
              color: INK,
            }}
          >
            Gallery
          </h1>
          <p style={{ color: MUTED, fontSize: 14.5, margin: 0 }}>
            Product, lifestyle, and community — a look at the PROMAG300 family.
          </p>
        </div>

        {/* Carousel. The ratio lives on the OUTER frame (not per slide) so the
            row height follows the visible slide; capped in width because a
            full-bleed square at the 1180px page width would stand ~1148px tall. */}
        <div
          style={{
            position: 'relative',
            maxWidth: 820,
            margin: '0 auto',
            background: '#fff',
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(0,0,0,.12)',
            aspectRatio: String(activeRatio),
            transition: 'aspect-ratio .35s cubic-bezier(.4,0,.2,1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              height: '100%',
              transition: 'transform .45s cubic-bezier(.4,0,.2,1)',
              transform: `translateX(-${slide * 100}%)`,
            }}
          >
            {SLIDES.map((src, i) => (
              <div key={src} style={{ flex: '0 0 100%', height: '100%', background: OL }}>
                {/* Only the current slide and its immediate neighbours are mounted.
                    `loading="lazy"` cannot carry this on its own: every slide sits
                    inside a transform-translated row, which browsers routinely
                    treat as in-viewport, so all 14 downloaded up front. The empty
                    div keeps the flex box's width so the slide transition is
                    unchanged. */}
                {Math.abs(i - slide) <= 1 && (
                  <img
                    src={src}
                    alt={`PROMAG300 ${i + 1}`}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      if (!img.naturalWidth || !img.naturalHeight) return;
                      const r = Math.min(
                        RATIO_MAX,
                        Math.max(RATIO_MIN, img.naturalWidth / img.naturalHeight),
                      );
                      setRatios((prev) => (prev[i] === r ? prev : { ...prev, [i]: r }));
                    }}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <CarouselBtn side="left" onClick={() => go(-1)}>
            <ChevronLeft size={24} />
          </CarouselBtn>
          <CarouselBtn side="right" onClick={() => go(1)}>
            <ChevronRight size={24} />
          </CarouselBtn>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            marginTop: 16,
            flexWrap: 'wrap',
          }}
        >
          {SLIDES.map((_, i) => (
            <button
              key={i}
              aria-label={`Slide ${i + 1}`}
              onClick={() => setSlide(i)}
              style={{
                height: 10,
                width: i === slide ? 26 : 10,
                borderRadius: 999,
                border: 0,
                cursor: 'pointer',
                background: i === slide ? O : '#d6d6d6',
                transition: 'width .2s, background .2s',
              }}
            />
          ))}
        </div>

        {/* Testimonials — designed image-cards */}
        <section style={{ marginTop: 56 }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2 style={{ fontSize: 26, fontWeight: 800, color: O, margin: '0 0 8px' }}>
              Testimonials
            </h2>
            <p style={{ color: MUTED, fontSize: 14.5, margin: 0 }}>
              Real stories from the PROMAG300 community.
            </p>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
              gap: 18,
            }}
          >
            {TESTI.map((src, i) => (
              <div
                key={src}
                style={{
                  borderRadius: 12,
                  overflow: 'hidden',
                  border: `1px solid ${LINE}`,
                  boxShadow: '0 8px 30px rgba(0,0,0,.08)',
                }}
              >
                {/* Intrinsic size (the sources are square) so the grid reserves the
                    row height before the image lands and nothing shifts. */}
                <img
                  src={src}
                  alt={`Testimonial ${i + 1}`}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={800}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Social band */}
        <section
          style={{
            marginTop: 56,
            background: '#fff',
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            padding: '40px 20px',
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontSize: 22, fontWeight: 800, color: INK, margin: '0 0 20px' }}>
            Visit Our Official PROMAG300 Page
          </h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
            {ICONS.map(([key, icon]) => (
              <a
                key={key}
                href={SOCIAL[key]}
                target="_blank"
                rel="noreferrer"
                aria-label={key}
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: OL,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img src={icon} alt="" style={{ width: 24, height: 24 }} />
              </a>
            ))}
          </div>
        </section>
      </div>
    </UpmiShell>
  );
}

function CarouselBtn({
  side,
  onClick,
  children,
}: {
  side: 'left' | 'right';
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={side === 'left' ? 'Previous' : 'Next'}
      style={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        left: side === 'left' ? 14 : undefined,
        right: side === 'right' ? 14 : undefined,
        width: 52,
        height: 52,
        borderRadius: '50%',
        background: '#fff',
        color: O,
        border: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 10px rgba(0,0,0,.22)',
      }}
    >
      {children}
    </button>
  );
}
